import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from '../common';
import VideoPlayer from './VideoPlayer';
import { useLocation } from 'react-router-dom';
import StudentVideoCourses from './StudentVideoCourses';

/**
 * Student view component
 */
const StudentView = ({ dealId }) => {
  return <StudentVideoCourses dealId={dealId} />;
}

/**
 * Admin view component for listing all video courses
 */
const AdminView = () => {
  const { showNotification } = useNotification();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication token not found');
          setLoading(false);
          return;
        }
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Fetch video courses
        const response = await axios.get('/api/video-courses', config);
        setCourses(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching video courses:', err);
        setError('Failed to load video courses: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };
    
    fetchCourses();
  }, []);

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setCurrentVideoIndex(0); // Reset to first video when selecting a course
  };
  
  const handleProgressUpdate = (videoId, progressData) => {
    // Update local course data to reflect progress
    if (selectedCourse && selectedCourse.video_sources) {
      const updatedSources = selectedCourse.video_sources.map(source => {
        if (source.id === videoId) {
          return { ...source, progress: progressData };
        }
        return source;
      });
      
      setSelectedCourse({
        ...selectedCourse,
        video_sources: updatedSources
      });
    }
  };
  
  const goToNextVideo = () => {
    if (selectedCourse?.video_sources && currentVideoIndex < selectedCourse.video_sources.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };
  
  const goToPreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };
  
  if (loading) {
    return <div className="text-center p-8">Loading video courses...</div>;
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        {error}
      </div>
    );
  }
  
  if (courses.length === 0) {
    return (
      <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-xl font-medium text-gray-700 mb-2">No Video Courses Available</h3>
        <p className="text-gray-500">
          There are currently no video courses available. Please check back later.
        </p>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {/* Course List Sidebar */}
      <div className="md:col-span-3 bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium mb-4 border-b pb-2">Available Courses</h3>
        
        <ul className="space-y-2">
          {courses.map(course => (
            <li key={course.id}>
              <button
                onClick={() => handleCourseSelect(course)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                  selectedCourse && selectedCourse.id === course.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="font-medium">{course.name}</div>
                {course.description && (
                  <div className="text-xs text-gray-500 mt-1">{course.description}</div>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Selected Course View */}
      <div className="md:col-span-9">
        {selectedCourse ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-2">{selectedCourse.name}</h2>
            
            {selectedCourse.description && (
              <div className="text-gray-600 mb-6">{selectedCourse.description}</div>
            )}
            
            {selectedCourse.video_sources && selectedCourse.video_sources.length > 0 ? (
              <div>
                {/* Video navigation and progress */}
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-gray-500">
                    Lesson {currentVideoIndex + 1} of {selectedCourse.video_sources.length}
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={goToPreviousVideo} 
                      disabled={currentVideoIndex === 0}
                      className={`px-3 py-1 rounded ${
                        currentVideoIndex === 0 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      Previous
                    </button>
                    <button 
                      onClick={goToNextVideo} 
                      disabled={currentVideoIndex >= selectedCourse.video_sources.length - 1}
                      className={`px-3 py-1 rounded ${
                        currentVideoIndex >= selectedCourse.video_sources.length - 1 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Next Lesson
                    </button>
                  </div>
                </div>
                
                {/* Current video */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  {(() => {
                    const video = selectedCourse.video_sources[currentVideoIndex];
                    return (
                      <>
                        <h3 className="text-lg font-medium mb-3">
                          {video.title || `Video ${currentVideoIndex + 1}`}
                        </h3>
                        
                        {video.description && (
                          <div className="text-gray-600 mb-4">{video.description}</div>
                        )}
                        
                        <VideoPlayer
                          courseId={selectedCourse.id}
                          videoId={video.id || `video-${currentVideoIndex}`}
                          videoUrl={video.url}
                          onProgressUpdate={(progress) => handleProgressUpdate(video.id || `video-${currentVideoIndex}`, progress)}
                        />
                      </>
                    );
                  })()}
                </div>
                
                {/* Video list/progress overview */}
                <div className="mt-6">
                  <h4 className="text-md font-medium mb-3">Course Progress</h4>
                  <ul className="space-y-2">
                    {selectedCourse.video_sources.map((video, index) => (
                      <li 
                        key={video.id || index} 
                        className={`p-2 rounded cursor-pointer ${
                          index === currentVideoIndex 
                            ? 'bg-blue-100 border-l-4 border-blue-600' 
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => setCurrentVideoIndex(index)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium">{video.title || `Lesson ${index + 1}`}</span>
                          </div>
                          <div className="text-sm">
                            {video.progress?.completed ? (
                              <span className="text-green-600">✓ Completed</span>
                            ) : video.progress?.percentage > 0 ? (
                              <span className="text-yellow-600">{video.progress.percentage}% complete</span>
                            ) : (
                              <span className="text-gray-400">Not started</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">
                  This course does not have any video content available yet.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2">Select a Course</h3>
            <p className="text-gray-500">
              Choose a course from the list to view its video content.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * VideoCoursesList component which determines whether to show student or admin view
 * based on presence of dealId
 */
const VideoCoursesList = ({ dealId: propDealId }) => {
  // Check URL for dealId parameter
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlDealId = queryParams.get('dealId');
  
  // Use dealId from props or URL
  const dealId = propDealId || urlDealId;
  
  // Render appropriate view based on dealId
  return dealId ? <StudentView dealId={dealId} /> : <AdminView />;
}

export default VideoCoursesList;